import React, { useMemo, useState } from "react";
import { Table, Button, Icon, Dropdown, Input, Checkbox, Message, Header, Statistic } from "semantic-ui-react";
import { ORIGENES } from "../catalogs/instrumentos";
import { gradoDe, recalcularBalance, planDeMedicion } from "../waterBalanceCalc";
import { BalanceResumenPanel } from "../BalanceResumenPanel";
import { useWaterBalanceT } from "../useWaterBalanceT";

const ORIGEN_OPTIONS = Object.values(ORIGENES).map((o) => ({ key: o.id, value: o.id, text: o.nombre }));
const LADO_OPTIONS = [
  { key: "entrada", value: "entrada" },
  { key: "salida", value: "salida" },
];

function filaVacia() {
  return { nombre: "", lado: "entrada", m3: "", origen: "estim_sin", calibracionVigente: false, evidencia: false };
}

// Modo "Cargar datos y generar": para plantas que ya tienen los números en
// una planilla. No hay herencia de Agua/Producción/Social — todo se declara
// acá, en una sola tabla. El motor (waterBalanceCalc.js) es el mismo que
// usa "Mi planta"; lo único que cambia es de dónde sale el dato.
//
// `step` es controlado desde afuera (BalanceSidebar + navPorModo.js): la
// navegación vive combinada con el selector de modo, no adentro de este
// workspace.
export function ModoDatosWorkspace({ step }) {
  const t = useWaterBalanceT();
  const [cabecera, setCabecera] = useState({ empresa: "", periodo: "", produccion: "", unidadProduccion: "kg" });
  const [filas, setFilas] = useState([filaVacia()]);
  const [tanque, setTanque] = useState({ inicial: "", final: "" });
  const [lossClassification, setLossClassification] = useState([]);
  const [cerrado, setCerrado] = useState(false);

  const actualizarFila = (idx, campo, val) => setFilas(filas.map((f, i) => (i === idx ? { ...f, [campo]: val } : f)));
  const agregarFila = () => setFilas([...filas, filaVacia()]);
  const quitarFila = (idx) => setFilas(filas.filter((_, i) => i !== idx));

  const flujos = useMemo(
    () =>
      filas
        .filter((f) => f.nombre && f.m3 !== "")
        .map((f, i) => ({
          id: `f${i}`,
          nombre: f.nombre,
          lado: f.lado,
          origen: f.origen,
          volumenM3: Number(f.m3) || 0,
          grado: gradoDe(f.origen, f.calibracionVigente, f.evidencia),
        })),
    [filas]
  );

  const resultado = useMemo(() => {
    const entradas = flujos.filter((f) => f.lado === "entrada");
    const salidas = flujos.filter((f) => f.lado === "salida");
    const deltaInventarioM3 = (Number(tanque.final) || 0) - (Number(tanque.inicial) || 0);
    return recalcularBalance({ entradas, salidas, deltaInventarioM3, cerrado, clasificacionResiduo: lossClassification, divergenciasSinResolver: 0 });
  }, [flujos, tanque, cerrado, lossClassification]);

  return (
    <div className="water-balance-workspace">
      <div className="water-balance-workspace__topbar">
        <span className="water-balance-workspace__tag">{t("water_balance_modo_datos_titulo")}</span>
        <span className="water-balance-workspace__name">{cabecera.empresa || t("water_balance_modo_datos_titulo")} · {cabecera.periodo}</span>
      </div>

      <div className="water-balance-workspace__main">
          {step === "cargar" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_datos_load_title")}</h3>
              <Message>{t("water_balance_datos_load_hint")}</Message>

              <Table basic="very" compact>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}>{t("water_balance_datos_company")}</Table.Cell>
                    <Table.Cell><Input fluid value={cabecera.empresa} onChange={(e) => setCabecera({ ...cabecera, empresa: e.target.value })} /></Table.Cell>
                    <Table.Cell width={4}>{t("water_balance_datos_period")}</Table.Cell>
                    <Table.Cell><Input fluid value={cabecera.periodo} onChange={(e) => setCabecera({ ...cabecera, periodo: e.target.value })} /></Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t("water_balance_production_label")}</Table.Cell>
                    <Table.Cell><Input fluid type="number" value={cabecera.produccion} onChange={(e) => setCabecera({ ...cabecera, produccion: e.target.value })} /></Table.Cell>
                    <Table.Cell>{t("water_balance_datos_production_unit")}</Table.Cell>
                    <Table.Cell><Input fluid value={cabecera.unidadProduccion} onChange={(e) => setCabecera({ ...cabecera, unidadProduccion: e.target.value })} /></Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>

              <Table celled compact style={{ marginTop: 16 }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{t("water_balance_col_name")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_datos_col_side")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_col_volume")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_col_origin")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_datos_col_calibration_ok")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_col_evidence")}</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filas.map((f, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell><Input transparent value={f.nombre} onChange={(e) => actualizarFila(idx, "nombre", e.target.value)} /></Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          selection
                          compact
                          options={LADO_OPTIONS.map((o) => ({ ...o, text: t(o.value === "entrada" ? "water_balance_datos_side_in" : "water_balance_datos_side_out") }))}
                          value={f.lado}
                          onChange={(_, d) => actualizarFila(idx, "lado", d.value)}
                        />
                      </Table.Cell>
                      <Table.Cell><Input type="number" transparent value={f.m3} onChange={(e) => actualizarFila(idx, "m3", e.target.value)} /></Table.Cell>
                      <Table.Cell>
                        <Dropdown selection compact options={ORIGEN_OPTIONS} value={f.origen} onChange={(_, d) => actualizarFila(idx, "origen", d.value)} />
                      </Table.Cell>
                      <Table.Cell><Checkbox checked={f.calibracionVigente} onChange={(_, d) => actualizarFila(idx, "calibracionVigente", d.checked)} /></Table.Cell>
                      <Table.Cell><Checkbox checked={f.evidencia} onChange={(_, d) => actualizarFila(idx, "evidencia", d.checked)} /></Table.Cell>
                      <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => quitarFila(idx)} /></Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <Button basic onClick={agregarFila}><Icon name="plus" /> {t("water_balance_datos_add_row")}</Button>

              <Header as="h4" style={{ marginTop: 24 }}>{t("water_balance_col_tank")} — Δ {t("water_balance_col_delta_inventory")}</Header>
              <Table basic="very" compact>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}>{t("water_balance_col_initial_level")} (m³)</Table.Cell>
                    <Table.Cell><Input type="number" fluid value={tanque.inicial} onChange={(e) => setTanque({ ...tanque, inicial: e.target.value })} /></Table.Cell>
                    <Table.Cell width={4}>{t("water_balance_col_final_level")} (m³)</Table.Cell>
                    <Table.Cell><Input type="number" fluid value={tanque.final} onChange={(e) => setTanque({ ...tanque, final: e.target.value })} /></Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>

              <Checkbox
                toggle
                label={t("water_balance_mark_loading_done")}
                checked={cerrado}
                onChange={(_, d) => setCerrado(d.checked)}
                style={{ marginTop: 16 }}
              />
            </div>
          )}

          {step === "balance" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_datos_step_balance")}</h3>
              <BalanceResumenPanel resultado={resultado} lossClassification={lossClassification} onChangeClasificacion={setLossClassification} />
            </div>
          )}

          {step === "informe" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_datos_step_informe")}</h3>
              <Statistic size="small"><Statistic.Value>{resultado.indiceCalidad.toFixed(2)}</Statistic.Value><Statistic.Label>{t("water_balance_index_out_of_5")}</Statistic.Label></Statistic>

              <Header as="h4">{t("water_balance_pareto_title")}</Header>
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{t("water_balance_col_use")}</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">{t("water_balance_col_cumulative_pct")}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {resultado.pareto.map((s) => (
                    <Table.Row key={s.id} positive={s.dentroDel80}>
                      <Table.Cell>{s.nombre}</Table.Cell>
                      <Table.Cell textAlign="right">{s.volumenM3.toLocaleString("es-AR")}</Table.Cell>
                      <Table.Cell textAlign="right">{s.pct.toFixed(1)}%</Table.Cell>
                      <Table.Cell textAlign="right">{s.pctAcumulado.toFixed(1)}%</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              <Header as="h4">{t("water_balance_section_20")}</Header>
              {planDeMedicion(flujos).length === 0 ? (
                <Message positive>{t("water_balance_all_grade5")}</Message>
              ) : (
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{t("water_balance_col_flow")}</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">{t("water_balance_col_current_grade")}</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">{t("water_balance_col_estimated_gain")}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {planDeMedicion(flujos).map((f) => (
                      <Table.Row key={f.id}>
                        <Table.Cell>{f.nombre}</Table.Cell>
                        <Table.Cell textAlign="right">{f.grado}</Table.Cell>
                        <Table.Cell textAlign="right">+{f.gananciaIndiceEstimada}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          )}
      </div>
    </div>
  );
}

export default ModoDatosWorkspace;
